import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { faqs } from "@/mocks/faqs";

export default function FaqPage() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const jsonLd = useMemo(() => {
    const base = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: base },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${base}/faq` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }, []);

  return (
    <>
      <Seo
        title="常见问题 - 吃瓜速递 · 国产吃瓜视频热点资讯站"
        description="关于国产吃瓜视频热点资讯站吃瓜速递的常见问题解答：更新频率、内容栏目、查找方式、使用费用与反馈渠道等，尽在常见问题页面。"
        keywords="国产吃瓜视频,常见问题,更新频率,内容栏目,使用指南"
        path="/faq"
        jsonLd={jsonLd}
      />

      <div className="bg-foreground-950 pb-14 pt-24 md:pt-28">
        <div className="container-x">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-1.5 text-sm text-background-50/60">
            <Link to="/" className="transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">常见问题</span>
          </nav>
          <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">常见问题</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-background-50/75">
            关于吃瓜速递的使用疑问，在这里一次性解答。想了解更新频率、内容栏目或查找方式，往下看就对了。
          </p>
        </div>
      </div>

      <section className="container-x py-10 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="space-y-3">
              {faqs.map((item, idx) => {
                const open = openIdx === idx;
                return (
                  <div key={item.q} className="card overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenIdx(open ? null : idx)}
                      aria-expanded={open}
                      className="flex w-full items-center justify-between gap-4 p-4 text-left md:p-5"
                    >
                      <span className="flex items-start gap-3">
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-bold ${
                            open ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-700"
                          }`}
                        >
                          {idx + 1}
                        </span>
                        <span className="text-sm font-semibold leading-6 text-foreground-900 md:text-base">
                          {item.q}
                        </span>
                      </span>
                      <i
                        className={`shrink-0 text-xl text-foreground-400 transition-transform ${open ? "rotate-180" : ""}`}
                      ></i>
                    </button>
                    <div
                      className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-4 pb-5 pl-[3.75rem] text-sm leading-7 text-foreground-600 md:pl-16">
                          {item.a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <aside className="space-y-5">
            <div className="card p-5">
              <h2 className="text-sm font-semibold text-foreground-900">还没找到答案？</h2>
              <p className="mt-2 text-xs leading-5 text-foreground-600">
                如有其他疑问，欢迎通过关于我们页面的联系方式向我们反馈，我们会尽快回复。
              </p>
              <Link to="/about" className="btn-outline mt-4 w-full">
                联系我们
              </Link>
            </div>
            <div className="card bg-accent-100/70 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className="ri-fire-fill text-xl"></i>
              </span>
              <h2 className="mt-3 text-sm font-semibold text-foreground-900">立即开吃</h2>
              <p className="mt-1.5 text-xs leading-5 text-foreground-600">
                去看看今天的最新热点事件和八卦汇总吧。
              </p>
              <Link to="/events" className="btn-primary mt-4 w-full">
                进入事件库
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}